/* eslint-disable prefer-const */

/* eslint-disable max-lines */
import { FormComponentProps } from 'src/components/FormComponents';

const COVERAGE_CODES = {
  PERSONAL_PROPERTY_LIABILITY: '1003',
  LOSS_OF_USE: '1004',
  PERSONAL_LIABILITY: '1005',
  MEDICAL: '1006',
  PET: '1007',
  WATER: '1008',
  IDENTITY: '1065',
};

const DEDUCTIBLE_CODES = {
  DEDUCTIBLE: '1',
  THEFT: '2',
  HURRICANE: '3',
  WIND_HAIL: '5',
  EARTHQUAKE: '6',
};

const DEFAULT_PERSONAL_PROPERTY_COVERAGE = 10_000;
const DEFAULT_PERSONAL_LIABILITY_COVERAGE = 100_000;

export const CoveragesDetailsForm = (
  data: any,
  appData: any,
): FormComponentProps => {
  let personalPropertyCoverage = 10000;
  let personalPropertyCoverageRange = [10000, 300000];
  let personalLiabilityCoverage = 100000;
  let personalLiabilityCoverageRange = [100000];
  let lossOfUseCoverageRange = [3000, 5000];
  let medicalPayments = 1000;
  let medicalPaymentsRange = [1000, 5000];

  let waterBackup = 0;
  let petDamage = 0;
  let identityFraud = 0;
  let earthQuake = 0;
  let deductible = 1000;
  let deductibleRange = [250, 500, 1000];
  let theft = 0;
  let theftRange = undefined;

  let wind = 0;
  let windRange = undefined;
  let petDamageRange = undefined;
  let waterBackupRange = undefined;
  let identityFraudRange = undefined;

  let earthquakeRange: any = [];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let hurricanePercentageValues: any = [];
  let hurricaneRange: any = [];

  if (data) {
    if (data.productCoverageList) {
      const personalPropertyLiability = data.productCoverageList.find(
        (coverage: { coverageCd: string }) =>
          coverage.coverageCd === COVERAGE_CODES.PERSONAL_PROPERTY_LIABILITY,
      );

      if (
        personalPropertyLiability &&
        Array.isArray(personalPropertyLiability.limitList)
      ) {
        personalPropertyCoverageRange = personalPropertyLiability.limitList.map(
          (limit: string) => parseInt(limit),
        );

        if (!personalPropertyCoverageRange.length) {
          // <Remark>
          // The only way for personalPropertyCoverageRange to be an empty array,
          // the personalPropertyLiability.limitList must be also empty array.
          // So, the following expression results in [undefined]. Is there a reason for it to exist?
          personalPropertyCoverageRange = [
            personalPropertyLiability.limitList.pop(),
          ];
        }

        personalPropertyCoverage =
          appData.personalPropertyCoverage ||
          getDefaultCoverage(
            personalPropertyCoverageRange,
            DEFAULT_PERSONAL_PROPERTY_COVERAGE,
          );
      }

      const lossOfUse = data.productCoverageList.find(
        (coverage: { coverageCd: string }) =>
          coverage.coverageCd === COVERAGE_CODES.LOSS_OF_USE,
      );
      const isLossOfUseLimitsExist =
        lossOfUse &&
        Array.isArray(lossOfUse.limitList) &&
        lossOfUse.limitList.length;

      if (isLossOfUseLimitsExist) {
        lossOfUseCoverageRange = lossOfUse.limitList.map((coverage: string) =>
          parseInt(coverage),
        );

        if (!lossOfUseCoverageRange.length) {
          lossOfUseCoverageRange = [lossOfUse.limitList.pop()];
        }
      }

      const personalLiability = data.productCoverageList.find(
        (coverage: { coverageCd: string }) =>
          coverage.coverageCd === COVERAGE_CODES.PERSONAL_LIABILITY,
      );
      const isPersonalLiabilityLimitsExist =
        personalLiability &&
        Array.isArray(personalLiability.limitList) &&
        personalLiability.limitList.length;

      if (isPersonalLiabilityLimitsExist) {
        personalLiabilityCoverageRange = personalLiability.limitList.map(
          (a: string) => parseInt(a),
        );
        if (!personalLiabilityCoverageRange.length) {
          personalLiabilityCoverageRange = [personalLiability.limitList.pop()];
        }
        personalLiabilityCoverage =
          appData.personalLiabilityCoverage ||
          getDefaultCoverage(
            personalLiabilityCoverageRange,
            DEFAULT_PERSONAL_LIABILITY_COVERAGE,
          );
      }

      const medical = data.productCoverageList.find(
        (a: { coverageCd: string }) => a.coverageCd === COVERAGE_CODES.MEDICAL,
      );

      if (
        medical &&
        Array.isArray(medical.limitList) &&
        medical.limitList.length
      ) {
        medicalPaymentsRange = medical.limitList.map((a: string) =>
          parseInt(a),
        );
        if (!medicalPaymentsRange.length) {
          medicalPaymentsRange = [medical.limitList.pop()];
        }
        medicalPayments =
          appData.medicalPayments || medicalPaymentsRange.slice(-1)[0];
      }

      const pet = data.productCoverageList.find(
        (a: { coverageCd: string }) => a.coverageCd === COVERAGE_CODES.PET,
      );

      if (pet && Array.isArray(pet.limitList) && pet.limitList.length) {
        petDamageRange = [0, parseInt(pet.limitList[0])];
        petDamage = appData.petDamage || petDamageRange[0];
      }
      const water = data.productCoverageList.find(
        (a: { coverageCd: string }) => a.coverageCd === COVERAGE_CODES.WATER,
      );
      if (water && Array.isArray(water.limitList) && water.limitList.length) {
        waterBackupRange = [0, parseInt(water.limitList[0])];
        waterBackup = appData.waterBackup || waterBackupRange[0];
      }
      const identity = data.productCoverageList.find(
        (a: { coverageCd: string }) => a.coverageCd === COVERAGE_CODES.IDENTITY,
      );
      if (
        identity &&
        Array.isArray(identity.limitList) &&
        identity.limitList.length
      ) {
        identityFraudRange = [0, parseInt(identity.limitList[0])];
        identityFraud = appData.identityFraud || identityFraudRange[0];
      }
    }

    if (data.productDeductibleList) {
      const deduct = data.productDeductibleList.find(
        (a: { deductibleCd: string }) =>
          a.deductibleCd === DEDUCTIBLE_CODES.DEDUCTIBLE,
      );
      if (deduct) {
        const maxLimit = Number.MAX_VALUE;
        deductibleRange = deduct.deductibleOptionList
          .map((a: string) => parseInt(a))
          .filter((a: number) => a <= maxLimit);
        if (!deductibleRange.length || appData?.address?.state === 'FL') {
          deductibleRange = [deduct.deductibleOptionList[0]];
        }
        deductible = appData.deductible || deductibleRange[0];
      }
      const theftf = data.productDeductibleList.find(
        (a: { deductibleCd: string }) =>
          a.deductibleCd === DEDUCTIBLE_CODES.THEFT,
      );

      if (theftf) {
        theftRange = theftf.deductibleOptionList.map((a: string) =>
          parseInt(a),
        );
        theft = parseInt(theftf.deductibleOptionList[0]) || appData.theft;
      }

      const hurricaneProductFound = data.productDeductibleList.find(
        (a: { deductibleCd: string }) =>
          a.deductibleCd === DEDUCTIBLE_CODES.HURRICANE,
      );

      if (hurricaneProductFound) {
        hurricaneProductFound.deductibleOptionList.forEach((num: any) => {
          if (num > 0 && num < 1) {
            hurricanePercentageValues.push(parseFloat(num));
          } else if (num > 1) {
            hurricaneRange.push(parseFloat(num));
          }
        });
        hurricaneRange.sort();
      }
      const earthQuakeProductFound = data.productDeductibleList.find(
        (a: { deductibleCd: string }) =>
          a.deductibleCd === DEDUCTIBLE_CODES.EARTHQUAKE,
      );
      if (earthQuakeProductFound) {
        earthQuakeProductFound.deductibleOptionList.forEach((num: any) => {
          if (num > 0) {
            earthquakeRange.push(parseFloat(num));
          }
        });
        earthquakeRange = [earthquakeRange[0] * 5000];
        earthQuake = appData.earthquake || earthquakeRange[0];
      }

      const windHail = data.productDeductibleList.find(
        (a: { deductibleCd: string }) =>
          a.deductibleCd === DEDUCTIBLE_CODES.WIND_HAIL,
      );
      if (windHail) {
        windRange = windHail.deductibleOptionList.map((a: string) =>
          parseInt(a),
        );
        wind = parseInt(windHail.deductibleOptionList[0]);
      }
    }
  }

  const CoveragesArray: any = [
    {
      key: 'personalPropertyCoverage',
      type: 'Coverages',
      templateOptions: {
        initalValue: personalPropertyCoverage,
        title: 'Personal Property',
        image: '/assets/images/personal_property.svg',
        possibleValues: personalPropertyCoverageRange,
        description:
          'Protect your belongings, including furniture, clothing, electronics.',
        tabs: [
          {
            title: 'Details',
            body: "<div style='display:flex;margin-bottom: 16px;'><span>Personal property coverage compensates you if your stuff is damaged or stolen.</span></div><div style='display:flex;margin-bottom: 16px;'><span>The amount your stuff is insured for depends on how much your stuff is worth (actual cash value) and policy limits.</span></div><div style='display:flex;margin-bottom: 16px;'><span>Expensive items like jewelry and art typically have lower coverage limits (usually $1500 to $2500). For an additional cost, you can increase those limits by adding blanket coverage or scheduling individual valuables.</span></div>",
          },
        ],
      },
    },
    {
      key: 'personalLiabilityCoverage',
      type: 'Coverages',
      templateOptions: {
        initalValue: personalLiabilityCoverage,
        title: 'Personal Liability',
        image: '/assets/images/personal_liability.svg',
        possibleValues: personalLiabilityCoverageRange,
        description:
          "Should someone suffer personal damage on your property and take legal action against you, this is the amount we'll cover.",
        tabs: [
          {
            title: 'Details',
            body: "<div style='display:flex;margin-bottom: 16px;'><span>Renters insurance will also cover costs you may be legally responsible for paying.</span></div><div style='display:flex;margin-bottom: 16px;'><span>If a visitor slips and falls or gets bitten by your dog, your policy’s personal liability and medical coverage will cover the cost of their medical bills.</span></div>",
          },
        ],
      },
    },
    {
      key: 'medicalPayments',
      type: 'Coverages',
      templateOptions: {
        initalValue: medicalPayments,
        title: 'Medical Liability',
        image: '/assets/images/medical.svg',
        possibleValues: medicalPaymentsRange,
        tabs: [
          {
            title: 'Details',
            body: "<div style='display:flex;margin-bottom: 16px;'><span>Medical Liability pays for bodily injury or property damage done to someone else.</span></div><div style='display:flex;margin-bottom: 16px;'><span>That someone could be a person, corporation, or entity.</span></div><div style='display:flex;margin-bottom: 16px;'><span>Medical payments through renters insurance helps ameliorate bodily injury without having to determine who was responsible, and avoids having to work through the process of a liability claim.</span></div>",
          },
        ],
        description:
          "If a visitor is injured on your property, this is the amount we'll pay towards their medical expenses.",
      },
    },
    {
      key: 'lossOfUse',
      type: 'Coverages',
      templateOptions: {
        title: 'Loss Of Use',
        readonly: true,
        image: '/assets/images/loss_of_use.svg',
        possibleValues: lossOfUseCoverageRange,
        description:
          "If your home becomes unlivable, we'll help you find a place to stay and cover your expenses up to the following amount.",
        dependsUpon: {
          type: 'loss_of_use',
          key: 'personalPropertyCoverage',
        },
        tabs: [
          {
            title: 'Details',
            body: "<div style='margin-bottom: 16px;display:flex;'>This compensates you if your apartment becomes uninhabitable due to a covered incident. </div><div style='margin-bottom: 16px;display:flex;'>Loss of use is 20% of personal property coverage in all states except Florida (10%), Connecticut (30%), and Hawaii (30%).</div>",
          },
        ],
      },
    },
    {
      key: 'deductible',
      type: 'Coverages',
      templateOptions: {
        initalValue: deductible,
        title: 'Deductible',
        image: '/assets/images/decutible.svg',
        description:
          'This is the amount you pay out of pocket on approved claims.',
        tabs: [
          {
            title: 'Details',
            body: "<div style='margin-bottom: 16px;display:flex;'>This is the amount you have to contribute towards a loss claim. </div><div style='margin-bottom: 16px;display:flex;'>Your deductible is subtracted from your claim payout, and applies to all types of loss (unless a separate deductible is noted in your policy).</div>",
          },
        ],
        possibleValues: deductibleRange,
      },
    },
  ];

  if (theft) {
    CoveragesArray.push({
      key: 'theft',
      type: 'Coverages',
      templateOptions: {
        initalValue: theft,
        title: 'Theft deductible',
        image: '/assets/images/theft.svg',
        description:
          'The amount you’ll be responsible for paying out of pocket when your home is robbed or a similar event. ',
        tabs: [
          {
            title: 'Details',
            body: "<div style='margin-bottom: 16px;display:flex;'>Your deductible is the amount you have to contribute towards a claim if your items are stolen. </div><div style='margin-bottom: 16px;display:flex;'>Your deductible will be subtracted from your claim payout, and applies to your loss as a result of various forms of theft such as in-home theft, travel theft, or vacation theft.</div>",
          },
        ],
        possibleValues: theftRange,
      },
    });
  }

  if (wind && windRange) {
    CoveragesArray.push({
      key: 'windHail',
      type: 'Coverages',
      templateOptions: {
        initalValue: wind,
        title: 'Wind and Hail Deductible',
        image: '/assets/images/wind.svg',
        description:
          'A separate, deductible provision that applies to loss caused by wind or hail triggered if your home is damaged by wind or rain from a hurricane',
        possibleValues: windRange,
        dependsUpon: {
          type: 'hide/show',
          key: 'deductible',
          typeConstraints: {
            maxValue: 250,
          },
        },
      },
    });
  }
  if (hurricaneRange && hurricaneRange[0]) {
    CoveragesArray.push({
      key: 'hurricane',
      type: 'Coverages',
      templateOptions: {
        initalValue: hurricaneRange[0],
        title: 'Hurricane Deductible',
        image: 'assets/images/hurricane.svg',
        readonly: true,
        description:
          'This is the amount you pay out of pocket on approved claims due to hurricanes. The deductible for this coverage 2 percent of your personal property coverage.',
        dependsUpon: {
          type: 'hide/show',
          key: 'deductible',
        },
      },
    });
  }
  if (
    waterBackupRange ||
    petDamageRange ||
    identityFraudRange ||
    earthquakeRange
  ) {
    CoveragesArray.push({
      key: 'heading',
      type: 'Heading',
      templateOptions: {
        title: 'Optional Coverage',
        subText: '',
      },
    });
    if (waterBackupRange) {
      CoveragesArray.push({
        key: 'waterBackup',
        type: 'Coverages',
        templateOptions: {
          initalValue: waterBackup,
          optional: true,
          title: 'Water Backup',
          image: '/assets/images/water.svg',
          possibleValues: waterBackupRange,
          description:
            'This covers water damage to your personal property as a result of backed-up sewers or drains.',
        },
      });
    }
    if (earthquakeRange.length >= 1) {
      CoveragesArray.push({
        key: 'earthquake',
        type: 'Coverages',
        templateOptions: {
          initalValue: earthQuake,
          title: 'Earthquake Deductible',
          image: 'assets/images/earthquake.svg',
          description:
            'This is the amount you pay out of pocket on approved claims due to earthquakes. The deductible for this coverage is $750.',
          possibleValues: earthquakeRange,
        },
      });
    }
    if (petDamageRange) {
      CoveragesArray.push({
        key: 'petDamage',
        type: 'Coverages',
        templateOptions: {
          initalValue: petDamage,
          optional: true,
          title: 'Pet Damages',
          image: '/assets/images/pet.svg',
          possibleValues: petDamageRange,
          description:
            'This covers up to $500 over your security deposit or special pet damage deposit if your pet damages your rental.',
        },
      });
    }
    if (identityFraudRange) {
      CoveragesArray.push({
        key: 'identityFraud',
        type: 'Coverages',
        templateOptions: {
          initalValue: identityFraud,
          optional: true,
          title: 'Identity Fraud',
          image: '/assets/images/identity.svg',
          possibleValues: identityFraudRange,
          description:
            'This covers up to $5,000 in losses as the direct result of identity fraud discovered during the policy period.',
        },
      });
    }
  }

  return {
    form_id: 'coverages',
    form_name: 'coverages',
    layout: 'full',
    formItems: [
      {
        id: 'coverages',
        title: 'Coverages',
        description:
          'Protect the items inside your home, such as furniture, clothing, and electronics.',
        field: {
          key: '',
          type: 'FormGroup',
          templateOptions: {
            rowItemCount: 12 * 12,
            className: 'form-group',
            items: CoveragesArray,
          },
        },
      },
    ],
    actionClassName: 'flex-direction-row-reverse',
    actions: [
      {
        label: 'Continue',
        type: 'submit',
      },
    ],
  };
};

const getDefaultCoverage = (
  coverageRange: number[],
  defaultValue: number,
): number => {
  if (coverageRange.indexOf(defaultValue) > -1) {
    return defaultValue;
  }
  const nextCoverage = coverageRange.find(
    (coverage) => coverage > defaultValue,
  );
  if (nextCoverage) {
    return nextCoverage;
  }
  return coverageRange[0];
};
